import { Typography } from "@mui/material";
import { CartesianGrid, Label, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CHART_COLORS } from "../../constants/ChartColors";
import { CustomLegend } from "./CustomLegend";
import { convertToPixelFromVH, convertToPixelFromVW } from "../../utils/ViewportUtils";
import { useContext, useState, useMemo } from "react";
import { ViewportContext } from "../../contexts/viewportContext";

const CHAR_PIXEL_SIZE = 2;

export default function LineChartContainer({ visual, enalrged = false }
  : { visual: any, enalrged?: boolean }) {

  const vpContext = useContext(ViewportContext);

  const largeLabelLength = (Math.max(...visual.x_series.map((xl: any) => xl.length)));
  const adjustHeight = largeLabelLength * CHAR_PIXEL_SIZE

  const [maxLengthForYLabel, setMaxLengthForYLabel] = useState(0);

  const adjustYLabelWidth = useMemo(() => {
    if (vpContext?.is4k)
      return maxLengthForYLabel * 1;
    if (vpContext?.is2k)
      return maxLengthForYLabel * 0.75;
    return maxLengthForYLabel * 0.25
  }, 
  [maxLengthForYLabel, vpContext?.is2k, vpContext?.is4k]);

  const height = enalrged ? 35 + adjustHeight / 2 : 20 + adjustHeight / 3;
  const margins = { left: convertToPixelFromVW(adjustYLabelWidth), bottom: convertToPixelFromVH(adjustHeight / 3) };

  const dataKey = Object.keys(visual.x_label)[0];
  const xAxisLabel = visual.x_label[dataKey];

  const data = () => {
    const converted_data = visual.x_series.map((s: any, index: number) => {
      return {
        [dataKey]: s,
        ...getDataRow(index)
      }
    })
    return converted_data;
  };

  const getDataRow = (index: number) => {
    const d = Object.keys(visual.y_label).map((s) => {
      const value = visual.y_series[s as keyof typeof visual.y_series][index];
      if (value.toString().length > maxLengthForYLabel) setMaxLengthForYLabel(value.toString().length)
      return {
        [s]: value
      }
    })
    return d.reduce((acc, obj) => {
      return { ...acc, ...obj };
    }, {});
  }

  return (
    <>
      {
        !enalrged && <Typography variant='h6' textAlign={'center'}>
          {visual.title}
        </Typography>
      }
      <ResponsiveContainer width="100%" height={convertToPixelFromVH(height)}>
        <LineChart data={data()} margin={margins}>
          <CartesianGrid strokeDasharray="2 2" strokeWidth={convertToPixelFromVW(0.075)} vertical={false} />
          <XAxis dataKey={dataKey} angle={-45} textAnchor="end" tick={{ color: '#000000', fontWeight: 700 }}>
            <Label value={xAxisLabel} position={'bottom'} offset={convertToPixelFromVW(largeLabelLength) * 0.25} />
          </XAxis>
          <YAxis tick={{ color: '#000000', fontWeight: 700 }} />
          <Tooltip labelStyle={{ color: '#000000' }} />
          <Legend verticalAlign="top"
            margin={{ bottom: 10 }}
            iconType="circle"
            align="center"
            layout="vertical"
            content={<CustomLegend />} />
          {
            Object.keys(visual.y_label).map((s, index) => {
              return <Line
                key={`line-${index}`}
                type="monotone"
                dataKey={s}
                strokeWidth={convertToPixelFromVW(0.125)}
                stroke={CHART_COLORS[index]}
                dot={false}
                activeDot={{ r: 8 }} />
            })
          }
        </LineChart>
      </ResponsiveContainer>
    </>)
}

